import React, { useState } from 'react';
import { Smartphone, Share, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { cn } from '@/lib/utils';

/**
 * Tasteful “add to home screen” guidance — honest, no app-store claims.
 */
export function AddToHomeScreenCard({ className, variant = 'dashboard' }) {
  const { canPromptInstall, isStandalone, isIos, isAndroid, promptInstall, showGuidance } = usePwaInstall();
  const [installing, setInstalling] = useState(false);

  if (isStandalone) return null;

  const handleInstall = async () => {
    setInstalling(true);
    try {
      await promptInstall();
    } finally {
      setInstalling(false);
    }
  };

  const compact = variant === 'compact';

  return (
    <div
      className={cn(
        'rounded-2xl border border-[#e8e2d8]/90 bg-[#fffcf7]/90 p-5 shadow-sm sm:p-6',
        className
      )}
      data-testid="add-to-home-screen-card"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#e8e2d8] bg-[#faf8f5] text-[#8b7355]">
          <Smartphone className="h-5 w-5" strokeWidth={1.35} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#8b7355]">
            {compact ? 'Quick access' : 'Add to phone'}
          </p>
          <h3 className="mt-1 font-serif text-lg font-normal text-[#1c1917]">
            Add MyLegacyBridge to your phone home screen
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">
            Use MyLegacyBridge on desktop, tablet or phone. Add it to your home screen for quick access when visiting
            family, recording memories, or reviewing the archive.
          </p>

          {showGuidance && (
            <ul className="mt-3 space-y-1.5 text-xs leading-relaxed text-[#6c6258]">
              {isIos ? (
                <li className="flex items-start gap-2">
                  <Share className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8b7355]" aria-hidden />
                  <span>
                    <span className="font-medium text-[#4a433c]">On iPhone:</span> tap Share, then Add to Home Screen.
                  </span>
                </li>
              ) : null}
              {isAndroid ? (
                <li className="flex items-start gap-2">
                  <Download className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8b7355]" aria-hidden />
                  <span>
                    <span className="font-medium text-[#4a433c]">On Android:</span> use Install app or Add to Home
                    screen when prompted.
                  </span>
                </li>
              ) : null}
              {!isIos && !isAndroid ? (
                <li>
                  On your phone browser, use your device&apos;s add-to-home-screen option. No app store download
                  required.
                </li>
              ) : null}
            </ul>
          )}

          {canPromptInstall ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4 rounded-full border-[#c9a961]/40 bg-[#fffaf3] text-[#3d3833] hover:bg-[#f5efe6]"
              onClick={handleInstall}
              disabled={installing}
              data-testid="pwa-install-btn"
            >
              {installing ? 'Opening…' : 'Install app'}
            </Button>
          ) : (
            <p className="mt-3 text-[11px] leading-relaxed text-[#8b7355]">
              Not available in this browser yet. Follow the steps above when using your phone.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddToHomeScreenCard;
